// src/components/trip/VirtualizedPhotoGrid.tsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FixedSizeGrid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import PhotoCard from './PhotoCard';
import { View, Text } from '@aws-amplify/ui-react';

interface VirtualizedPhotoGridProps {
  photos: any[];
  isSelectionMode: boolean;
  selectedPhotos: Set<string>;
  photoUrls: { [key: string]: string };
  thumbnailUrls: { [key: string]: string };
  userRole: string | null; // Add userRole to props
  onPhotoSelect: (photoId: string, event: React.MouseEvent) => void;
  onPhotoClick: (photoId: string, index: number) => void;
  onVisiblePhotosChange?: (visiblePhotoIds: string[]) => void;
}

// Calculate how many columns to show based on container width
const getColumnCount = (width: number) => {
  if (width < 600) return 2;
  if (width < 960) return 3;
  return 4;
};

// Memoized Cell component to reduce unnecessary rerenders
const CellComponent = React.memo(({ columnIndex, rowIndex, style, data }: any) => {
  const {
    photos,
    columnCount,
    onPhotoSelect,
    onPhotoClick,
    isSelectionMode,
    selectedPhotos,
    photoUrls,
    thumbnailUrls,
    userRole // Make userRole available to the cell
  } = data;

  const index = rowIndex * columnCount + columnIndex;

  if (index >= photos.length) {
    return <div style={style} />;
  }

  const photo = photos[index];

  return (
    <div
      key={`cell-${photo.id}`} // Add stable key
      style={{
        ...style,
        padding: '8px',
        boxSizing: 'border-box'
      }}
      className="grid-cell"
    >
      <PhotoCard
        key={photo.id} // Add stable key
        photo={photo}
        index={index}
        isSelectionMode={isSelectionMode}
        isSelected={selectedPhotos.has(photo.id)}
        userRole={userRole} // Pass userRole to the PhotoCard
        onSelect={onPhotoSelect}
        onClick={onPhotoClick}
      />
    </div>
  );
});

CellComponent.displayName = 'CellComponent';

const VirtualizedPhotoGrid: React.FC<VirtualizedPhotoGridProps> = ({
  photos,
  isSelectionMode,
  selectedPhotos,
  photoUrls,
  thumbnailUrls,
  userRole,
  onPhotoSelect,
  onPhotoClick,
  onVisiblePhotosChange
}) => {
  // Track visible row indices
  const [visibleRowIndices, setVisibleRowIndices] = useState<{ start: number, end: number }>({ start: 0, end: 0 });
  const [visibleColumnIndices, setVisibleColumnIndices] = useState<{ start: number, end: number }>({ start: 0, end: 0 });
  const [currentColumnCount, setCurrentColumnCount] = useState(4);
  const gridRef = useRef<any>(null);

  // Calculate row height based on desired aspect ratio and column width
  const getRowHeight = (width: number, columnCount: number) => {
    const columnWidth = width / columnCount;
    // Use aspect ratio for photos plus some padding
    return columnWidth * 0.8 + 16;
  };

  // Compute visible photo IDs whenever the visible range changes
  useEffect(() => {
    if (!onVisiblePhotosChange) return;

    const visiblePhotoIds: string[] = [];

    for (let rowIndex = visibleRowIndices.start; rowIndex <= visibleRowIndices.end; rowIndex++) {
      for (let colIndex = visibleColumnIndices.start; colIndex <= visibleColumnIndices.end; colIndex++) {
        const index = rowIndex * currentColumnCount + colIndex;
        if (index < photos.length) {
          visiblePhotoIds.push(photos[index].id);
        }
      }
    }

    // Notify parent component about visible photos
    if (visiblePhotoIds.length > 0) {
      onVisiblePhotosChange(visiblePhotoIds);
    }
  }, [
    visibleRowIndices,
    visibleColumnIndices,
    currentColumnCount,
    photos,
    onVisiblePhotosChange
  ]);


  // Memoize item data to prevent unnecessary rerenders
  const itemData = useMemo(() => ({
    photos,
    columnCount: currentColumnCount,
    onPhotoSelect,
    onPhotoClick,
    isSelectionMode,
    selectedPhotos,
    photoUrls,
    thumbnailUrls,
    userRole // Include userRole in itemData
  }), [
    photos,
    currentColumnCount,
    onPhotoSelect,
    onPhotoClick,
    isSelectionMode,
    selectedPhotos,
    photoUrls,
    thumbnailUrls,
    userRole
  ]);

  const onItemsRendered = ({
    visibleRowStartIndex,
    visibleRowStopIndex,
    visibleColumnStartIndex,
    visibleColumnStopIndex
  }: any) => {
    setVisibleRowIndices({
      start: visibleRowStartIndex,
      end: visibleRowStopIndex
    });

    setVisibleColumnIndices({
      start: visibleColumnStartIndex,
      end: visibleColumnStopIndex
    });
  };

  if (photos.length === 0) {
    return (
      <View textAlign="center" padding="3rem" backgroundColor="#f5f5f5" borderRadius="medium">
        <Text fontSize="1.2rem">No photos in this trip yet.</Text>
      </View>
    );
  }

  return (
    <div className="virtualized-grid-container">
      <AutoSizer>
        {({ height, width }) => {
          const columnCount = getColumnCount(width);
          const rowCount = Math.ceil(photos.length / columnCount);
          const rowHeight = getRowHeight(width, columnCount);

          // Update current column count for visibility calculations
          if (columnCount !== currentColumnCount) {
            setCurrentColumnCount(columnCount);
          }

          return (
            <FixedSizeGrid
              ref={gridRef}
              columnCount={columnCount}
              columnWidth={width / columnCount}
              height={height}
              rowCount={rowCount}
              rowHeight={rowHeight}
              width={width}
              itemData={itemData}
              onItemsRendered={onItemsRendered}
              overscanRowCount={2}
              overscanColumnCount={1}
              className="photo-grid"
            >
              {CellComponent}
            </FixedSizeGrid>
          );
        }}
      </AutoSizer>
    </div>
  );
};

export default VirtualizedPhotoGrid;